import {go} from "@/router/Router.ts";
import {ElementMeasure, getElementMeasure} from "@fd/helpers/DomUtils.ts";
import {SrcType} from "@t/meta/SourceType.ts";

export function isJQuery(obj: any): obj is JQuery<HTMLElement> {
	return typeof jQuery !== "undefined" && obj instanceof jQuery;
}

export function un$(elm:HTMLElement|NodeList|JQuery):HTMLElement|HTMLElement[]|null {
	let element: HTMLElement | HTMLElement[] | null = null;
	if (elm instanceof HTMLElement) {
		// It's already a plain DOM element
		element = elm;
	} else if (elm instanceof NodeList) {
		if (elm.length === 1) element = elm.item(0) as HTMLElement;
		else if (elm.length > 1) {
			element = [];
			for (const el of elm) {
				element.push(el as HTMLElement);
			}
		}
	} else if (isJQuery(elm)) {
		// It's a jQuery object, extract the first DOM element
		if (elm.length === 1) element = elm[0];
		else if (elm.length > 1) {
			element = [];
			for (let i = 0; i < elm.length; i++) {
				element.push(elm[i]);
			}
		}
	}
	return element;
}


type ParsedUri = { file:string, queryKey:Record<string, string|undefined> };

function parseUri (str:string):ParsedUri {
	const o = parseUri.options,
		m = o.parser[o.strictMode ? "strict" : "loose"].exec(str),
		uri = {};
	let i = 14;

	while (i--) uri[o.key[i]] = m[i] || "";

	uri[o.q.name] = {};
	uri[o.key[12]].replace(o.q.parser, function ($0, $1, $2) {
		if ($1) uri[o.q.name][$1] = $2;
	});
	return uri as ParsedUri;
}

parseUri.options = {
	strictMode: false,
	key: ["source","protocol","authority","userInfo","user","password","host","port","relative","path","directory","file","query","anchor"],
	q:   {
		name:   "queryKey",
		parser: /(?:^|&)([^&=]*)=?([^&]*)/g
	},
	parser: {
		strict: /^(?:([^:\/?#]+):)?(?:\/\/((?:(([^:@]*):?([^:@]*))?@)?([^:\/?#]*)(?::(\d*))?))?((((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
		loose:  /^(?:(?![^:@]+:[^:@\/]*@)([^:\/?#.]+):)?(?:\/\/)?((?:(([^:@]*):?([^:@]*))?@)?([^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/
	}
};


export function previewKey(srcType: number, id?: number|string, srcSubType?: number) {
	let sfx = "";
	if (typeof srcSubType === "number" && srcSubType !== 0) sfx = "-"+srcSubType;
	switch (srcType) {
		case SrcType.TASK:
			return "ta-"+id + sfx;
		case SrcType.EVENT:
		case SrcType.REPEATED_EVENT:
			return "ev-"+id + sfx;
		case SrcType.EMAIL:
			return "em-"+id + sfx;
		case SrcType.OPPORTUNITY:
			return "op-"+id + sfx;
		case SrcType.BUSCASE:
			return "bc-" + id + sfx;
		case SrcType.CONTACT:
			return "co-"+id + sfx;
		case SrcType.PARTY:
		case SrcType.BRANCH:
			return "cu-"+id + sfx;
		case SrcType.PROBLEM:
			return "hd-"+id + sfx;
		case SrcType.USER:
			return "us-"+id + sfx;
		case SrcType.INVOICE:
			return "in-"+id + sfx;
		case SrcType.PRODUCT:
			return "pr-"+id + sfx;
		case SrcType.CONTENT:
			return "cd-"+id + sfx;
		case SrcType.OFFER:
			return "of-"+id + sfx;
		case SrcType.TEMPLATE:
			return "tm-"+id + sfx;
		case SrcType.STORE:
			return "st-"+id + sfx;
		case SrcType.MOTION:
			return "mo-"+id + sfx;
		case SrcType.SMS:
			return "sms-"+id + sfx;
		case SrcType.GIFT:
			return "gift-"+id + sfx;
		case SrcType.BUSCASE_ITEM:
			return "bci-"+id + sfx;
		case SrcType.OFFER_ITEM:
			return "ofi-"+id + sfx;
		case SrcType.TRANSFER:
			return "tr-"+id + sfx;
		case SrcType.ATTFILE:
			return "file-"+id + sfx;
		case SrcType.ATTFILE_ATTACH:
			return "att-"+id + sfx;
		case SrcType.KNOWLEDGE:
			return "kn-"+id + sfx;
		case SrcType.BULK_EMAIL:
			return "bulk-"+id + sfx;
		case SrcType.RELATION:
			return "rel-"+id + sfx;
		case SrcType.CONSENT:
			return "consent-"+id + sfx;
		case SrcType.FACT:
			return "fact-"+id + sfx;
	}
	return "";
}


/**
 * Converts link (url) to preview key
 * @param {string} url
 * @return {string}
 */
export function linkToPreviewKey(url:string):string {
	if (!url) return null;
	if (url.toLowerCase().indexOf('http') !== 0) {
		if (!url.startsWith("/")) url = "/"+url;
		url = 'https://www.placeholder.cz' + url;
	}
	let key = null;
	if (url) {
		const parsed = parseUri(url);

		if (parsed.file === "editCustomer.do" || parsed.file === "editParty.do") {
			key = "cu-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "editOpportunity.do") {
			key = "op-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "editBusCase.do") {
			key = "bc-" + parsed.queryKey["id"];
			const bctype = parsed.queryKey["bctype"];
			if (bctype) {
				key = key + "-" + bctype;
			}
		}
		else if (parsed.file === "editEvent.do") {
			key = "ev-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "editTask.do") {
			key = "ta-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "readMailMessage.do") {
			key = "em-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "editProduct.do") {
			key = "pr-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "editContact.do") {
			key = "co-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "editProblem.do") {
			key = "hd-" + parsed.queryKey["id"];
		}
		else if (parsed.file === "editContent.do") {
			key = "ct-" + parsed.queryKey["id"];
		}
	}
	return key;
}

/** INIT **/

function findKey(e:HTMLElement) {
	let key = e.getAttribute("data-preview");
	if (key) return key;
	let mention = e.getAttribute("data-mention");
	if (mention) {
		return "cu-"+mention;
	}
	let url = e.getAttribute("href");
	return linkToPreviewKey(url);
}


const PREVIEW_DELAY = 400;
const PREVIEW_HIDE_DELAY = 200;

let previewElement:HTMLDivElement = null;
let	anchorElement:HTMLElement = null;
let anchorMeasure:ElementMeasure = null;

let showTimeout:number = 0;
let hideTimeout:number = 0;
let currKey:string = null;

let previewVisible:boolean = false;		// is preview actually visible on the screen
let isInAnchor:boolean = false;
let isInBoat:boolean = false;
let isInPreview = () => isInAnchor || isInBoat;


export function previewDiv():HTMLDivElement {
	if (previewElement == null) {
		previewElement = document.createElement("div");
		previewElement.id = "livePreview";
		previewElement.classList.add("livePreview");
		document.body.append(previewElement);
		previewElement.addEventListener("mouseenter", boatMouseEnter);
		previewElement.addEventListener("mouseleave", boatMouseLeave);
		previewElement.addEventListener("click", previewClick);
	}
	return previewElement;
}

interface CacheItem<K, D> { loaded:number; key: K, data: D; }
const cache:CacheItem<string, string>[] = [];
const MAX_AGE = 300000;	// 5 mins

function getFromCache(key: string):string|null {
	const index = cache.findIndex((ci) => {
		return ci.key === key;
	});
	if (index >= 0) {
		const item = cache[index];
		if ((Date.now() - item.loaded) < MAX_AGE) {
			if (index > 0) {
				// move item to the front of the
				cache.splice(index, 1);
				cache.splice(0, 0, item);
			}
			return item.data;
		} else {
			// expired item
			cache.splice(index, 1);	// remove from the cache
			return null;
		}
	}
	return null;
}

function storeToCache(key:string, data:string):void {
	cache.splice(0, 0, { loaded:Date.now(), key, data });
}

export async function loadPreviewData(key:string):Promise<string> {
	const cacheValue = getFromCache(currKey);
	if (cacheValue) {
		return cacheValue;
	} else {
		if (import.meta.env.DEV) {
			// Dev mode
			return `<div>
				<div class="prevTitleDiv">Web Preview</div>
				<div class="prevInfoDiv">You are seeing this message because you are running DEV mode of the app.</div>
				<div class="prevRefDiv">The key is ${key}</div>
			</div>`;
		}
		return await fetch("preview.do?key="+currKey)
			.then(res => res.text())
			.then((html) => {
				storeToCache(currKey, html);
				return html;
			});
	}
}


function timeToShowPreview() {
	if (currKey) {
		loadPreviewData(currKey).then(previewCallBack);
	}
}


function timeToHidePreview() {
	if (previewElement) previewDiv().style.visibility = "hidden";
	previewVisible = false;
	anchorElement = null;
	currKey = null;
	anchorMeasure = null;
}

function previewCallBack(html) {
	if (anchorElement && html && html !== "null" && html.length > 0) {
		const div = previewDiv();
		div.innerHTML = html;
		const m = anchorMeasure ?? getElementMeasure(anchorElement);
		showPreview(div, m);
	}
}

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

// preview width is 350px standard

function sideFits(center:number, viewport:number, previewSize:number):boolean {
	const halfPreview = previewSize / 2;
	return center >= halfPreview && (viewport - center) >= halfPreview;
}


function showPreview(div: HTMLDivElement, m:ElementMeasure) {
	const centerX = m.left + m.width / 2;
	const centerY = m.top + m.height / 2;
	const viewportCenterX = m.viewLeft + m.width / 2;
	const viewportCenterY = m.viewTop + m.height / 2;

	div.classList.remove("tail-top-center", "tail-bottom-center", "tail-left-center", "tail-right-center");
	const dm = getElementMeasure(div);

	const verticalFits = sideFits(viewportCenterY, m.viewportHeight, dm.height);
	const horizontalFits = sideFits(viewportCenterX, m.viewportWidth, dm.width);
	const fits = {
		top: m.spaceTop >= dm.height && horizontalFits,
		bottom: m.spaceBottom >= dm.height && horizontalFits,
		left: m.spaceLeft >= dm.width && verticalFits,
		right: m.spaceRight >= dm.width && verticalFits,
	};

	let placement: TooltipPlacement = null;

	if (fits.right) placement = "right";
	else if (fits.bottom) placement = "bottom";
	else if (fits.left) placement = "left";
	else if (fits.top) placement = "top";
	else {
		const spaces:{ space:number, p:TooltipPlacement}[] = [{space:m.spaceTop, p:"top"}, {space:m.spaceBottom, p:"bottom"}, {space:m.spaceLeft, p:"left"},{space:m.spaceRight, p:"right"} ];
		const max = spaces.reduce((a, b) => (a.space > b.space ? a : b));
		placement = max.p;
	}

	switch (placement) {
		case "top":
			div.style.top = (m.top - dm.height - 6) + "px";
			div.style.left = (centerX - dm.width / 2) + "px";
			div.classList.add("tail-bottom-center");
			break;
		case "right":
			div.style.top = (centerY - dm.height / 2) + "px";
			div.style.left = (m.left + m.width - 2) + "px";
			div.classList.add("tail-left-center");
			break;
		case "bottom":
			div.style.top = (m.top + m.height - 2) + "px";
			div.style.left = (centerX - dm.width / 2) + "px";
			div.classList.add("tail-top-center");
			break;
		case "left":
			div.style.top = (centerY - dm.height / 2) + "px";
			div.style.left = (m.left - dm.width - 6) + "px";
			div.classList.add("tail-right-center");
			break;
	}
	div.style.visibility = "visible";
	previewVisible = true;
}

function clearShowTimeout() {
	if (showTimeout) {
		clearTimeout(showTimeout);
		showTimeout = 0;
	}
}

function clearHideTimeout() {
	if (hideTimeout) {
		clearTimeout(hideTimeout);
		hideTimeout = 0;
	}
}

function clearTimeouts() {
	clearShowTimeout();
	clearHideTimeout();
}

export interface PreviewAnchor {
	element: HTMLElement;
	key: string;
	measure: ElementMeasure;
}

export function anchorMouseEnter(e?:MouseEvent, info?:PreviewAnchor):void {
	isInAnchor = true;
	clearTimeouts();
	if (info) {
		anchorElement = info.element;
		currKey = info.key;
		anchorMeasure = info.measure;
	} else {
		anchorElement = e.target as HTMLElement;
		currKey = findKey(anchorElement);
		anchorMeasure = null;	// will be measured on display
	}
	showTimeout = setTimeout(timeToShowPreview, PREVIEW_DELAY);
}

export function anchorMouseLeave():void {
	isInAnchor = false;
	clearTimeouts();
	hideTimeout = setTimeout(timeToHidePreview, PREVIEW_HIDE_DELAY);
}

export function anchorMouseDown():void {
	clearTimeouts();		// stop pending timeouts
	timeToHidePreview();	// remove already shown preview
}

function boatMouseEnter():void {
	isInBoat = true;
	clearTimeouts();
}

function boatMouseLeave():void {
	isInBoat = false;
	clearTimeouts();
	hideTimeout = setTimeout(timeToHidePreview, PREVIEW_HIDE_DELAY);
}

function previewClick(e:MouseEvent):void {
	const link = e.target as HTMLElement;
	if (link.tagName === "A") {
		// User clicked on the link
		const href = (link as HTMLAnchorElement).getAttribute("href");
		if (href) {
			e.preventDefault();
			e.stopPropagation();
			go(href)
		}
	}
}



/**
 * @param {jQuery} [rootElement] - root element to find previewable sub elements (null will search the whole page)
 */
export function initPreview(rootElement:HTMLElement|NodeList|JQuery|null):void {
	let root = rootElement ? un$(rootElement) : document.body;
	if (root) {
		if (!Array.isArray(root)) {
			root = [root];
		}
		for (let item of root) {
			const links = item.querySelectorAll("a, [data-preview]");
			const mentions = item.querySelectorAll("span, [data-mention]");
			initPreviewFor(links);
			initPreviewFor(mentions);
		}
	}
}

function initPreviewForOne(e:HTMLElement) {
	if (e.tagName === "A") {
		initLinkPreview(e as HTMLLinkElement);
	} else {
		initSpanPreview(e);
	}
}

export function initPreviewFor(e:HTMLElement|NodeList|JQuery):void {
	const els = un$(e);
	if (els) {
		if (Array.isArray(els)) {
			for (let el of els) {
				initPreviewForOne(el);
			}
		} else {
			initPreviewForOne(els);
		}
	}
}

export function removePreviewListeners(el:HTMLElement):void {
	el.removeEventListener("mouseenter", anchorMouseEnter);
	el.removeEventListener("mouseleave", anchorMouseLeave);
	el.removeEventListener("mousedown", anchorMouseDown);
}

export function addPreviewListeners(el:HTMLElement):void {
	el.addEventListener("mouseenter", anchorMouseEnter);
	el.addEventListener("mouseleave", anchorMouseLeave);
	el.addEventListener("mousedown", anchorMouseDown);
}

function initLinkPreview(link:HTMLLinkElement) {
	const key = findKey(link);
	if (key !== null) addPreviewListeners(link);
}


/**
 * Mentions preview support
 * @param {type} span
 * @returns {undefined}
 */
function initSpanPreview(span:HTMLElement) {
	const key = findKey(span);
	if (key !== null) {
		let partyId = span.getAttribute("data-mention");
		span.style.cursor = "pointer";
		span.addEventListener("mouseenter", anchorMouseEnter);
		span.addEventListener("mouseleave", anchorMouseLeave);
		span.addEventListener("click", () => {
			go('editParty.do?id=' + partyId);
		});
	}
}